import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "./index";
import { fetchRewriteSlots, RewriteSlot, setActiveRewriteSlot } from "../utils/api";

interface RewriteSlotState {
  currentSlotId: string | null;
  slots: RewriteSlot[];
  loading: boolean;
  syncingActiveSlot: boolean;
  error: string | null;
  /** Хотя бы один ответ loadRewriteSlots (успех или ошибка) — отличие от начального [] до первого fetch. */
  slotsHydrated: boolean;
}

const initialState: RewriteSlotState = {
  currentSlotId: null,
  slots: [],
  loading: false,
  syncingActiveSlot: false,
  error: null,
  slotsHydrated: false,
};

export const loadRewriteSlots = createAsyncThunk(
  "rewriteSlots/load",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchRewriteSlots();
      const raw = (response as any)?.data;
      if (Array.isArray(raw)) return raw;
      if (Array.isArray(raw?.slots)) return raw.slots;
      if (Array.isArray(raw?.rewriteSlots)) return raw.rewriteSlots;
      return [];
    } catch (error: any) {
      return rejectWithValue(error?.message || "Не удалось загрузить слоты");
    }
  }
);

export const syncActiveSlot = createAsyncThunk(
  "rewriteSlots/syncActive",
  async (slotId: string, { rejectWithValue }) => {
    try {
      await setActiveRewriteSlot(slotId);
      return slotId;
    } catch (error: any) {
      return rejectWithValue(error?.message || "Не удалось активировать слот");
    }
  }
);

const rewriteSlotSlice = createSlice({
  name: "rewriteSlots",
  initialState,
  reducers: {
    setCurrentSlotId: (state, action: PayloadAction<string | null>) => {
      state.currentSlotId = action.payload;
      state.error = null;
    },
    clearRewriteSlots: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadRewriteSlots.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadRewriteSlots.fulfilled, (state, action) => {
        state.loading = false;
        state.slotsHydrated = true;
        state.slots = action.payload;
        // Активный id больше не в списке (удалили слот и т.п.) — сбрасываем на первый или null.
        if (
          state.currentSlotId &&
          !action.payload.some((s) => s.id === state.currentSlotId)
        ) {
          state.currentSlotId = action.payload[0]?.id ?? null;
        }
        if (!state.currentSlotId && action.payload.length > 0) {
          state.currentSlotId = action.payload[0].id;
        }
      })
      .addCase(loadRewriteSlots.rejected, (state, action) => {
        state.loading = false;
        state.slotsHydrated = true;
        state.error = (action.payload as string) || "Не удалось загрузить слоты";
      })
      .addCase(syncActiveSlot.pending, (state) => {
        state.syncingActiveSlot = true;
      })
      .addCase(syncActiveSlot.fulfilled, (state) => {
        // Не перезаписываем currentSlotId безусловно: к моменту, когда сервер
        // ответил «slot активирован», пользователь мог уже переключиться на
        // другой слот или сбросить выбор (например, через «Создать новый
        // слот» — там currentSlotId намеренно ставится в null). Если поверх
        // этого положить stale-ответ (action.payload), эффекты в DocumentApp
        // снова дёрнут /files/parsed для предыдущего слота и пользователь
        // увидит ложную ошибку «Файлы документа не найдены на сервере».
        // Каноничное значение currentSlotId всегда выставляется инициатором
        // ДО dispatch syncActiveSlot, поэтому здесь оставляем стейт нетронутым.
        state.syncingActiveSlot = false;
      })
      .addCase(syncActiveSlot.rejected, (state, action) => {
        state.syncingActiveSlot = false;
        state.error = (action.payload as string) || "Не удалось активировать слот";
      });
  },
});

export const { setCurrentSlotId, clearRewriteSlots } = rewriteSlotSlice.actions;

export const selectRewriteSlotsState = (state: RootState) => state.rewriteSlots;
export const selectRewriteSlots = (state: RootState) => state.rewriteSlots.slots;
export const selectCurrentSlotId = (state: RootState) => state.rewriteSlots.currentSlotId;

export default rewriteSlotSlice.reducer;
